<template>
  <div v-if="config" class="access-form-page">
    <div v-if="successRecord" class="success-view">
      <button type="button" class="back-btn" @click="exitSuccess">
        <van-icon name="arrow-left" />
      </button>

      <div class="success-panel">
        <div class="success-icon">
          <van-icon name="success" color="#18c45c" size="66" />
        </div>
        <div class="success-title">{{ config.successMessage }}</div>
        <div class="success-meta">姓名：{{ successRecord.name || '-' }}</div>
        <div class="success-meta">
          {{ config.timeLabel }}：{{ formatTime(successRecord.passOrRegisterTime || successRecord.submitTime) }}
        </div>

        <div class="success-links">
          <button type="button" @click="openRecordDetail(successRecord)">查看填写详情</button>
          <span>|</span>
          <button type="button" @click="$toast('当前版本仅保留入口文字')">订阅后续动态</button>
        </div>

        <van-button round block class="success-action" @click="exitSuccess">
          完成，请点击退出
        </van-button>
      </div>
    </div>

    <template v-else>
      <section class="record-card">
        <div class="record-card__header">
          <h3>我的填写记录</h3>
        </div>

        <div v-if="latestRecord" class="record-summary" @click="openRecordDetail(latestRecord)">
          <div class="record-summary__time">
            {{ formatSummaryTime(latestRecord.passOrRegisterTime || latestRecord.submitTime) }}
            {{ latestRecord.name || latestRecord.plateNumber || '-' }} 填写了{{ config.formTitle }}
          </div>
          <div
            v-for="item in summaryRows(latestRecord)"
            :key="item.label"
            class="record-summary__meta"
          >
            {{ item.label }}：{{ item.value }}
          </div>
        </div>
        <van-empty v-else image="search" description="当前设备暂无填写记录" />

        <button type="button" class="record-card__footer" @click="showRecordsPopup = true">
          全部{{ records.length }}条
        </button>
      </section>

      <section class="form-card">
        <van-form @submit="openConfirmDialog">
          <template v-for="field in visibleFields">
            <div :key="field.key" class="form-block">
              <div class="field-label">
                {{ field.label }} <span v-if="field.required" class="required">*</span>
                <button
                  v-if="field.type === 'shipName' && shipNameHistory.length"
                  type="button"
                  class="history-link"
                  @click="showShipHistoryPopup = true"
                >
                  历史数据
                </button>
              </div>

              <div v-if="field.type === 'phone'" class="phone-row">
                <van-field
                  v-model.trim="form[field.key]"
                  readonly
                  clickable
                  placeholder="请点击右侧按钮获取微信手机号"
                  class="phone-field"
                />
                <van-button
                  class="fetch-phone-btn"
                  type="primary"
                  plain
                  round
                  :loading="fetchingPhone"
                  :disabled="fetchingPhone"
                  @click.prevent="fetchPhoneNumber"
                >
                  {{ fetchingPhone ? '获取中...' : '获取手机号' }}
                </van-button>
              </div>

              <van-field
                v-else-if="field.type === 'phoneInput'"
                v-model.trim="form[field.key]"
                type="tel"
                maxlength="11"
                clearable
                :placeholder="field.placeholder || '请输入11位手机号码'"
              />

              <van-field
                v-else-if="field.type === 'text' || field.type === 'idCard' || field.type === 'shipName'"
                v-model.trim="form[field.key]"
                :maxlength="field.maxlength || (field.type === 'idCard' ? 18 : 100)"
                clearable
                :placeholder="field.placeholder || `请输入${field.label}`"
                @blur="field.type === 'shipName' && applyLatestShipRecord()"
              />

              <van-field
                v-else-if="field.type === 'textarea'"
                v-model.trim="form[field.key]"
                type="textarea"
                rows="3"
                autosize
                :maxlength="field.maxlength || 200"
                show-word-limit
                :placeholder="field.placeholder || `请输入${field.label}`"
              />

              <van-field
                v-else-if="field.type === 'number'"
                v-model.trim="form[field.key]"
                type="number"
                clearable
                :placeholder="field.placeholder || `请输入${field.label}`"
              />

              <van-field
                v-else-if="field.type === 'select'"
                readonly
                clickable
                is-link
                :value="getOptionText(field.optionGroup, form[field.key])"
                :placeholder="field.placeholder || `请选择${field.label}`"
                @click="openSelect(field)"
              />

              <van-field
                v-else-if="field.type === 'datetime'"
                :value="formatTime(form[field.key])"
                readonly
                clickable
                is-link
                @click="openDateTimePicker(field)"
              />

              <div v-else-if="field.type === 'plate'">
                <car-plate-keyboard
                  :key="plateKeyboardRenderKey"
                  :car-number="form[field.key]"
                  active-color="#1556c7"
                  ok-btn-text="完成"
                  @tap-key="handlePlateKeyboardChange(field, $event)"
                  @tap-del="handlePlateKeyboardChange(field, $event)"
                  @tap-ok="handlePlateKeyboardOk(field, $event)"
                />
                <div v-if="plateNumberError" class="plate-inline-error">{{ plateNumberError }}</div>
                <div class="field-hint">使用车牌专用键盘录入，当前版本不接 OCR，后续可扩展识别能力。</div>
              </div>

              <div v-else-if="field.type === 'image'">
                <van-uploader
                  v-model="imageFileLists[field.key]"
                  :after-read="file => handleImageAfterRead(field, file)"
                  :max-count="1"
                  accept="image/*"
                  upload-text="拍照或从相册选择"
                />
                <div class="field-hint">{{ field.hint || '图片将在提交时正式上传后台。' }}</div>
              </div>

              <div v-if="field.recent && recentValue(field.key)" class="history-fill">
                <span>最近输入：{{ recentValue(field.key) }}</span>
                <button type="button" @click="form[field.key] = recentValue(field.key)">填入</button>
              </div>
            </div>
          </template>

          <div class="submit-wrap">
            <van-button round block native-type="submit" class="submit-btn">
              提交登记
            </van-button>
          </div>
        </van-form>
      </section>
    </template>

    <van-popup v-model="showConfirmPopup" position="center" round :style="{ width: '90%', maxWidth: '420px' }">
      <div class="custom-confirm-dialog">
        <div class="confirm-icon">
          <van-icon name="warning-o" color="#ffb020" size="52" />
        </div>
        <div class="confirm-title">请确认登记信息</div>
        <div class="confirm-details">
          <div v-for="item in confirmRows" :key="item.label" class="detail-item">
            <span class="detail-label">{{ item.label }}</span>
            <span class="detail-value">{{ item.value }}</span>
          </div>
        </div>
        <div class="confirm-actions">
          <van-button round type="default" @click="showConfirmPopup = false">取消</van-button>
          <van-button round color="#16a56f" :loading="submitting" @click="submitRegistration">确认提交</van-button>
        </div>
      </div>
    </van-popup>

    <van-popup v-model="showSelectPopup" position="bottom" round :style="{ maxHeight: '65%' }">
      <div class="picker-popup">
        <div class="picker-popup__header">
          <h3>{{ activeSelectField ? `选择${activeSelectField.label}` : '请选择' }}</h3>
          <van-icon name="cross" @click="showSelectPopup = false" />
        </div>
        <div
          v-for="item in activeSelectOptions"
          :key="item.code"
          class="picker-option"
          @click="selectOption(item.code)"
        >
          <span>{{ item.description }}</span>
          <van-icon v-if="activeSelectField && form[activeSelectField.key] === item.code" name="success" color="#16a56f" />
        </div>
      </div>
    </van-popup>

    <van-popup v-model="showDateTimePopup" position="bottom" round :style="{ maxHeight: '72%' }">
      <div class="datetime-popup">
        <div class="picker-popup__header">
          <h3>{{ activeDateTimeField ? `选择${activeDateTimeField.label}` : '选择时间' }}</h3>
          <van-icon name="cross" @click="showDateTimePopup = false" />
        </div>
        <van-datetime-picker
          v-model="dateTimePickerValue"
          type="datetime"
          title=""
          :show-toolbar="false"
          :min-date="minPassTime"
          :max-date="maxPassTime"
          :formatter="formatPassTimePickerColumn"
        />
        <div class="datetime-popup__actions">
          <van-button round type="default" @click="showDateTimePopup = false">取消</van-button>
          <van-button round color="#16a56f" @click="confirmDateTime">确定</van-button>
        </div>
      </div>
    </van-popup>

    <van-popup v-model="showRecordsPopup" position="bottom" round :style="{ height: '72%' }">
      <div class="record-popup">
        <div class="record-popup__header">
          <h3>我的填写记录</h3>
          <van-icon name="cross" @click="showRecordsPopup = false" />
        </div>
        <div v-if="records.length" class="record-list">
          <div
            v-for="record in records"
            :key="record.id || record.recordNo || record.submitTime"
            class="record-list__item"
            @click="openRecordDetail(record)"
          >
            <div class="record-list__title">{{ formatSummaryTime(record.passOrRegisterTime || record.submitTime) }}</div>
            <div class="record-list__desc">{{ summaryRows(record).map(item => item.value).filter(Boolean).join(' · ') }}</div>
          </div>
        </div>
        <van-empty v-else image="search" description="暂无填写记录" />
      </div>
    </van-popup>

    <van-popup v-model="showDetailPopup" position="bottom" round :style="{ height: '72%' }">
      <div class="record-popup">
        <div class="record-popup__header">
          <h3>填写详情</h3>
          <van-icon name="cross" @click="showDetailPopup = false" />
        </div>
        <div v-if="detailRecord" class="detail-list">
          <div v-for="field in visibleDetailFields(detailRecord)" :key="field.key" class="detail-row" :class="{ 'detail-row--block': field.type === 'image' }">
            <span>{{ field.label }}</span>
            <template v-if="field.type === 'image'">
              <img
                v-if="detailRecord[field.urlKey || `${field.key}Url`]"
                :src="detailRecord[field.urlKey || `${field.key}Url`]"
                :alt="field.label"
                class="detail-image"
                @click="previewImage(detailRecord[field.urlKey || `${field.key}Url`])"
              />
              <strong v-else>-</strong>
            </template>
            <strong v-else>{{ displayFieldValue(field, detailRecord) }}</strong>
          </div>
        </div>
      </div>
    </van-popup>

    <van-popup v-model="showShipHistoryPopup" position="bottom" round :style="{ maxHeight: '62%' }">
      <div class="picker-popup">
        <div class="picker-popup__header">
          <h3>选择历史船舶</h3>
          <van-icon name="cross" @click="showShipHistoryPopup = false" />
        </div>
        <div
          v-for="shipName in shipNameHistory"
          :key="shipName"
          class="picker-option"
          @click="selectShipName(shipName)"
        >
          <span>{{ shipName }}</span>
          <van-icon v-if="form.shipName === shipName" name="success" color="#16a56f" />
        </div>
      </div>
    </van-popup>
  </div>
</template>

<script>
import CarPlateKeyboard from 'vue-car-plate-keyboard/src/lib/carPlatePanel.vue';
import axios from '@/utils/request';
import globalDic, { fetchMultiDic } from '@/utils/dic';
import {
  appendModuleRecord,
  cacheRecentFormValues,
  findLatestRecordByShipName,
  getAccessRegistrationConfig,
  getCachedPhone,
  getDeviceVisitorId,
  getLatestRecord,
  getModuleRecords,
  getRecentValues,
  getShipNameHistory,
  mergeModuleRecords,
  setCachedPhone
} from '@/utils/accessRegistration';

function createForm(fields = []) {
  return fields.reduce((result, field) => {
    result[field.key] = field.type === 'datetime' ? new Date().toISOString() : '';
    return result;
  }, {});
}

function createImageFileLists(fields = []) {
  return fields
    .filter(field => field.type === 'image')
    .reduce((result, field) => {
      result[field.key] = [];
      return result;
    }, {});
}

export default {
  name: 'AccessRegistrationForm',
  components: {
    CarPlateKeyboard
  },
  props: {
    moduleType: {
      type: String,
      required: true
    }
  },
  data() {
    const config = getAccessRegistrationConfig(this.moduleType);
    return {
      config,
      deviceVisitorId: '',
      form: createForm(config ? config.fields : []),
      optionsMap: {},
      imageFileLists: createImageFileLists(config ? config.fields : []),
      imageFiles: {},
      records: [],
      latestRecord: null,
      detailRecord: null,
      successRecord: null,
      shipNameHistory: [],
      fetchingPhone: false,
      submitting: false,
      showConfirmPopup: false,
      showSelectPopup: false,
      showDateTimePopup: false,
      showRecordsPopup: false,
      showDetailPopup: false,
      showShipHistoryPopup: false,
      activeSelectField: null,
      activeDateTimeField: null,
      dateTimePickerValue: new Date(),
      plateKeyboardRenderKey: 0,
      plateNumberError: ''
    };
  },
  computed: {
    draftKey() {
      return `accessRegistrationDraft:${this.moduleType}`;
    },
    visibleFields() {
      return (this.config.fields || []).filter(field => this.isFieldVisible(field, this.form));
    },
    activeSelectOptions() {
      return this.activeSelectField ? this.getOptions(this.activeSelectField) : [];
    },
    confirmRows() {
      return this.visibleFields
        .filter(field => field.type !== 'image')
        .slice(0, 5)
        .map(field => ({
          label: field.label,
          value: this.displayFieldValue(field, this.form)
        }));
    },
    minPassTime() {
      const value = new Date();
      value.setFullYear(value.getFullYear() - 1);
      return value;
    },
    maxPassTime() {
      const value = new Date();
      value.setFullYear(value.getFullYear() + 2);
      return value;
    }
  },
  mounted() {
    this.deviceVisitorId = getDeviceVisitorId();
    this.restoreDraft();
    this.fillCachedPhone();
    this.consumePhoneFromRoute();
    this.bootstrapPage();
    window.addEventListener('beforeunload', this.persistDraft);
  },
  beforeDestroy() {
    window.removeEventListener('beforeunload', this.persistDraft);
  },
  watch: {
    moduleType() {
      this.reloadByModuleType();
    },
    '$route.query.mpPhone'() {
      this.consumePhoneFromRoute();
    },
    form: {
      handler() {
        this.persistDraft();
      },
      deep: true
    }
  },
  methods: {
    reloadByModuleType() {
      this.config = getAccessRegistrationConfig(this.moduleType);
      this.form = createForm(this.config ? this.config.fields : []);
      this.imageFileLists = createImageFileLists(this.config ? this.config.fields : []);
      this.imageFiles = {};
      this.plateKeyboardRenderKey += 1;
      this.restoreDraft();
      this.fillCachedPhone();
      this.bootstrapPage();
    },
    async bootstrapPage() {
      if (!this.config) {
        return;
      }

      const dicNames = this.config.dictionaries || [];
      let dicResult = {};
      if (dicNames.length) {
        try {
          dicResult = await fetchMultiDic(dicNames);
        } catch (error) {
          dicResult = {};
        }
      }

      const optionsMap = {};
      (this.config.fields || []).forEach(field => {
        if (field.type !== 'select') {
          return;
        }
        const fromRemote = field.dicName && dicResult[field.dicName] && dicResult[field.dicName].length
          ? dicResult[field.dicName]
          : field.dicName && globalDic[field.dicName] && globalDic[field.dicName].length
            ? globalDic[field.dicName]
            : [];
        optionsMap[field.optionGroup] = fromRemote.length ? fromRemote : (field.defaultOptions || []);
      });

      this.optionsMap = optionsMap;
      this.shipNameHistory = getShipNameHistory();
      await this.loadRecords();
    },
    persistDraft() {
      if (!this.config) {
        return;
      }

      localStorage.setItem(this.draftKey, JSON.stringify(this.form));
    },
    restoreDraft() {
      try {
        const draftText = localStorage.getItem(this.draftKey);
        if (draftText) {
          this.form = {
            ...createForm(this.config ? this.config.fields : []),
            ...JSON.parse(draftText)
          };
        }
      } catch (error) {
        localStorage.removeItem(this.draftKey);
      }
    },
    clearDraft() {
      localStorage.removeItem(this.draftKey);
    },
    fillCachedPhone() {
      (this.config.fields || [])
        .filter(field => field.type === 'phone')
        .forEach(field => {
          this.form[field.key] = this.form[field.key] || getCachedPhone() || '';
        });
    },
    isFieldVisible(field, data) {
      if (!field.showIf) {
        return true;
      }

      return data[field.showIf.key] === field.showIf.value;
    },
    visibleDetailFields(record) {
      return (this.config.fields || []).filter(field => this.isFieldVisible(field, record));
    },
    getOptions(fieldOrGroup) {
      const group = typeof fieldOrGroup === 'string' ? fieldOrGroup : fieldOrGroup.optionGroup;
      return this.optionsMap[group] || (fieldOrGroup.defaultOptions || []);
    },
    getOptionText(group, code) {
      const item = (this.optionsMap[group] || []).find(option => option.code === code);
      return item ? item.description : '';
    },
    displayFieldValue(field, record) {
      const value = record[field.key];
      if (field.type === 'select') {
        return this.getOptionText(field.optionGroup, value) || '-';
      }
      if (field.type === 'datetime') {
        return this.formatTime(value) || '-';
      }
      return value || '-';
    },
    summaryRows(record) {
      return (this.config.summaryFields || []).map(field => {
        let value = record[field.key];
        if (field.optionGroup) {
          value = this.getOptionText(field.optionGroup, value);
        }
        if (field.format === 'time') {
          value = this.formatTime(value);
        }
        return {
          label: field.label,
          value: value || '-'
        };
      });
    },
    recentValue(key) {
      if (key === 'name') {
        return getRecentValues(this.moduleType, 'name')[0] || '';
      }
      if (key === 'idCardNo') {
        return getRecentValues(this.moduleType, 'idCardNo')[0] || '';
      }
      return '';
    },
    validateIdCard(value) {
      return /(^\d{15}$)|(^\d{17}(\d|X|x)$)/.test(String(value || '').trim());
    },
    validatePhone(value) {
      return /^1\d{10}$/.test(String(value || '').trim());
    },
    validateTemperature(value) {
      return /^(3[0-9]|4[0-2])(\.\d)?$/.test(String(value || '').trim());
    },
    validatePlateNumber(value) {
      const plate = (value || '').toUpperCase();
      const regularPlate = /^[\u4e00-\u9fa5][A-Z][A-HJ-NP-Z0-9]{5}$/;
      const newEnergyPlate = /^[\u4e00-\u9fa5][A-Z](?:[A-HJ-NP-Z0-9]{5}[DF]|[DF][A-HJ-NP-Z0-9]{5})$/;
      const specialPlate = /^[\u4e00-\u9fa5][A-Z][A-HJ-NP-Z0-9]{4}[\u4e00-\u9fa5A-Z0-9挂学警港澳领使]$/;
      return regularPlate.test(plate) || newEnergyPlate.test(plate) || specialPlate.test(plate);
    },
    validateBeforeConfirm() {
      for (const field of this.visibleFields) {
        const value = this.form[field.key];
        if (field.required && field.type === 'image' && !this.imageFiles[field.key]) {
          this.$toast.fail(`请上传${field.label}`);
          return false;
        }
        if (field.required && field.type !== 'image' && !String(value || '').trim()) {
          this.$toast.fail(`请填写${field.label}`);
          return false;
        }
        if ((field.type === 'phone' || field.type === 'phoneInput') && value && !this.validatePhone(value)) {
          this.$toast.fail(`请输入正确的${field.label}`);
          return false;
        }
        if (field.type === 'idCard' && value && !this.validateIdCard(value)) {
          this.$toast.fail(`请输入正确的${field.label}`);
          return false;
        }
        if (field.type === 'number' && field.key === 'temperature' && value && !this.validateTemperature(value)) {
          this.$toast.fail('请输入正确的体温');
          return false;
        }
        if (field.type === 'plate' && !this.validatePlateBeforeSubmit(field)) {
          return false;
        }
      }
      return true;
    },
    validatePlateBeforeSubmit(field) {
      const plateNumber = (this.form[field.key] || '').toUpperCase();
      if (!plateNumber) {
        this.plateNumberError = '请输入车牌号';
        this.$toast.fail(this.plateNumberError);
        return false;
      }
      if (!this.validatePlateNumber(plateNumber)) {
        this.plateNumberError = '请输入正确的中国车牌号';
        this.$toast.fail(this.plateNumberError);
        return false;
      }
      this.plateNumberError = '';
      this.form[field.key] = plateNumber;
      return true;
    },
    openConfirmDialog() {
      if (!this.validateBeforeConfirm()) {
        return;
      }
      this.showConfirmPopup = true;
    },
    openSelect(field) {
      this.activeSelectField = field;
      this.showSelectPopup = true;
    },
    selectOption(code) {
      if (!this.activeSelectField) {
        return;
      }
      this.form[this.activeSelectField.key] = code;
      this.clearHiddenDependentValues(this.activeSelectField.key);
      this.showSelectPopup = false;
    },
    clearHiddenDependentValues(changedKey) {
      (this.config.fields || []).forEach(field => {
        if (field.showIf && field.showIf.key === changedKey && !this.isFieldVisible(field, this.form)) {
          this.form[field.key] = '';
          delete this.imageFiles[field.key];
          this.$set(this.imageFileLists, field.key, []);
        }
      });
    },
    openDateTimePicker(field) {
      this.activeDateTimeField = field;
      const current = new Date(this.form[field.key]);
      this.dateTimePickerValue = Number.isNaN(current.getTime()) ? new Date() : current;
      this.showDateTimePopup = true;
    },
    confirmDateTime() {
      if (this.activeDateTimeField) {
        this.form[this.activeDateTimeField.key] = this.dateTimePickerValue.toISOString();
      }
      this.showDateTimePopup = false;
    },
    formatPassTimePickerColumn(type, value) {
      const unitMap = { year: '年', month: '月', day: '日', hour: '时', minute: '分' };
      return unitMap[type] ? `${value}${unitMap[type]}` : value;
    },
    handlePlateKeyboardChange(field, payload = {}) {
      this.form[field.key] = String(payload.value || '').toUpperCase();
      if (this.plateNumberError && this.form[field.key]) {
        this.plateNumberError = '';
      }
    },
    handlePlateKeyboardOk(field, payload = {}) {
      this.handlePlateKeyboardChange(field, payload);
      if (!payload.valid && this.form[field.key]) {
        this.plateNumberError = '请输入正确的中国车牌号';
      }
    },
    handleImageAfterRead(field, file) {
      this.$set(this.imageFiles, field.key, file.file);
    },
    async loadRecords() {
      let records = getModuleRecords(this.moduleType);
      try {
        const response = await axios.post('/api/AccessRegistration/MyRecords', {
          moduleType: this.moduleType,
          deviceVisitorId: this.deviceVisitorId,
          pageSize: 20
        });

        if (response && response.data && response.data.success) {
          const items = (response.data.data && response.data.data.items) || [];
          records = mergeModuleRecords(this.moduleType, items);
        }
      } catch (error) {
        records = mergeModuleRecords(this.moduleType, records);
      }
      this.records = records;
      this.latestRecord = getLatestRecord(this.moduleType);
    },
    openRecordDetail(record) {
      if (!record) {
        return;
      }
      this.detailRecord = record;
      this.showRecordsPopup = false;
      this.showDetailPopup = true;
    },
    async submitRegistration() {
      if (this.submitting) {
        return;
      }

      this.submitting = true;
      this.$toast.loading({ message: '提交中...', forbidClick: true, duration: 0 });

      try {
        const formData = new FormData();
        formData.append('moduleType', this.moduleType);
        formData.append('formTypeName', this.config.formTitle);
        formData.append('deviceVisitorId', this.deviceVisitorId);
        formData.append('source', 'h5');
        formData.append('isPublicSubmit', 'true');
        Object.entries(this.form).forEach(([key, value]) => {
          formData.append(key, value || '');
        });
        Object.entries(this.imageFiles).forEach(([key, file]) => {
          if (file) {
            formData.append(key, file);
          }
        });
        formData.append('formDataJson', JSON.stringify(this.form));
        formData.append('imageDataJson', JSON.stringify(this.buildImageMeta()));
        formData.append('successMessage', this.config.successMessage);
        formData.append('previewSummary', this.summaryRows(this.form).map(item => `${item.label}:${item.value}`).join('；'));

        const response = await axios.post('/api/AccessRegistration/Add', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        if (!response || !response.data || !response.data.success) {
          this.$toast.fail((response && response.data && response.data.message) || '提交失败');
          return;
        }

        const record = {
          ...this.form,
          ...(response.data.data || {}),
          moduleType: this.moduleType,
          formTypeName: this.config.formTitle,
          submitTime: (response.data.data && response.data.data.submitTime) || new Date().toISOString()
        };

        appendModuleRecord(this.moduleType, record);
        cacheRecentFormValues(this.moduleType, this.form);
        this.shipNameHistory = getShipNameHistory();
        this.records = getModuleRecords(this.moduleType);
        this.latestRecord = getLatestRecord(this.moduleType);
        this.successRecord = record;
        this.showConfirmPopup = false;
        this.clearDraft();
      } catch (error) {
        this.$toast.fail('提交失败，请稍后重试');
      } finally {
        this.submitting = false;
        this.$toast.clear();
      }
    },
    buildImageMeta() {
      return Object.keys(this.imageFiles).map(key => {
        const field = (this.config.fields || []).find(item => item.key === key) || {};
        return {
          fieldKey: key,
          imageType: field.imageType || key,
          label: field.label
        };
      });
    },
    isMiniProgramWebView() {
      if (typeof window === 'undefined') {
        return false;
      }
      const ua = window.navigator.userAgent || '';
      const hasBridge = !!(window.wx && window.wx.miniProgram);
      return hasBridge || window.__wxjs_environment === 'miniprogram' || /miniprogram/i.test(ua);
    },
    getMiniProgramBridge() {
      if (typeof window === 'undefined' || !window.wx || !window.wx.miniProgram) {
        return null;
      }
      return window.wx.miniProgram;
    },
    requestPhoneFromMiniProgram() {
      const miniProgram = this.getMiniProgramBridge();
      if (!miniProgram || typeof miniProgram.navigateTo !== 'function') {
        return false;
      }
      this.persistDraft();
      const target = this.$route.fullPath || this.config.path;
      miniProgram.navigateTo({
        url: `/pages/phone-auth/index?target=${encodeURIComponent(target)}`
      });
      return true;
    },
    consumePhoneFromRoute() {
      const { mpPhone, mpPhoneError } = this.$route.query || {};
      if (mpPhone && this.validatePhone(mpPhone)) {
        (this.config.fields || [])
          .filter(field => field.type === 'phone')
          .forEach(field => {
            this.form[field.key] = String(mpPhone).trim();
          });
        setCachedPhone(mpPhone);
        this.clearPhoneBridgeQuery();
        this.$toast.success('手机号已自动填入');
        return;
      }
      if (mpPhoneError) {
        const message = decodeURIComponent(mpPhoneError);
        this.clearPhoneBridgeQuery();
        this.$toast.fail(message || '获取手机号失败');
      }
    },
    clearPhoneBridgeQuery() {
      const query = { ...(this.$route.query || {}) };
      delete query.mpPhone;
      delete query.mpPhoneError;
      delete query.mpPhoneSource;
      this.$router.replace({ path: this.$route.path, query }).catch(() => {});
    },
    async fetchPhoneNumber() {
      if (this.fetchingPhone) {
        return;
      }
      this.fetchingPhone = true;
      try {
        if (this.isMiniProgramWebView() && this.requestPhoneFromMiniProgram()) {
          this.$toast.loading({ message: '正在拉起微信手机号授权', forbidClick: true, duration: 1200 });
          return;
        }
        this.$toast.fail('当前环境不支持微信手机号授权');
      } finally {
        this.fetchingPhone = false;
      }
    },
    selectShipName(shipName) {
      this.form.shipName = shipName;
      this.showShipHistoryPopup = false;
      this.applyLatestShipRecord();
    },
    applyLatestShipRecord() {
      const latest = findLatestRecordByShipName(this.form.shipName);
      if (!latest) {
        return;
      }
      this.form.name = this.form.name || latest.name || '';
      this.form.idCardNo = this.form.idCardNo || latest.idCardNo || '';
      this.form.phoneNumber = this.form.phoneNumber || latest.phoneNumber || '';
      if (latest.phoneNumber) {
        setCachedPhone(latest.phoneNumber);
      }
    },
    previewImage(url) {
      if (!url) {
        return;
      }
      if (window.ImagePreview) {
        window.ImagePreview([url]);
        return;
      }
      this.$imagePreview([url]);
    },
    formatTime(value) {
      if (!value) {
        return '';
      }
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return value;
      }
      const yy = date.getFullYear();
      const mm = `${date.getMonth() + 1}`.padStart(2, '0');
      const dd = `${date.getDate()}`.padStart(2, '0');
      const hh = `${date.getHours()}`.padStart(2, '0');
      const mi = `${date.getMinutes()}`.padStart(2, '0');
      return `${yy}-${mm}-${dd} ${hh}:${mi}`;
    },
    formatSummaryTime(value) {
      if (!value) {
        return '';
      }
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return value;
      }
      const now = new Date();
      const sameDay = now.getFullYear() === date.getFullYear() && now.getMonth() === date.getMonth() && now.getDate() === date.getDate();
      const hh = `${date.getHours()}`.padStart(2, '0');
      const mi = `${date.getMinutes()}`.padStart(2, '0');
      return sameDay ? `今天 ${hh}:${mi}` : `${date.getMonth() + 1}月${date.getDate()}日 ${hh}:${mi}`;
    },
    exitSuccess() {
      this.successRecord = null;
      this.resetForm();
      this.loadRecords();
    },
    resetForm() {
      this.form = createForm(this.config.fields || []);
      this.fillCachedPhone();
      this.imageFileLists = createImageFileLists(this.config.fields || []);
      this.imageFiles = {};
      this.plateKeyboardRenderKey += 1;
      this.plateNumberError = '';
      this.clearDraft();
    }
  }
};
</script>

<style scoped>
.access-form-page,
.success-view {
  min-height: 100%;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.record-card,
.form-card {
  padding: 10px 12px;
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.94);
  border: 1px solid rgba(215, 229, 251, 0.96);
  box-shadow: var(--shadow-card);
}

.record-card__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
}

.record-card__header h3 {
  margin: 0;
  font-size: 18px;
  color: var(--text-primary);
}

.record-summary {
  padding: 10px 12px;
  border-radius: 14px;
  border: 1px solid #e4edf9;
  background: #fff;
}

.record-summary__time {
  color: #8c8f96;
  line-height: 1.35;
  margin-bottom: 6px;
  font-size: 12px;
}

.record-summary__meta {
  color: #1f2633;
  font-size: 14px;
  line-height: 1.4;
  font-weight: 600;
}

.record-card__footer {
  margin-top: 8px;
  width: 100%;
  border: none;
  border-radius: 12px;
  background: #f7f9fc;
  color: #5c7ca7;
  font-size: 14px;
  line-height: 38px;
}

.form-block + .form-block {
  margin-top: 18px;
}

.field-label {
  margin-bottom: 10px;
  color: var(--text-primary);
  font-size: 15px;
  font-weight: 700;
}

.required {
  color: #ee0a24;
}

.history-link {
  margin-left: 8px;
  border: none;
  background: transparent;
  color: #1d5faf;
  font-size: 13px;
  font-weight: 700;
}

.history-fill {
  margin-top: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  color: var(--text-muted);
  font-size: 12px;
}

.history-fill button {
  border: none;
  padding: 6px 12px;
  border-radius: 999px;
  color: #1d5faf;
  background: #edf4ff;
  font-size: 12px;
  font-weight: 700;
}

.phone-row {
  display: flex;
  gap: 10px;
  align-items: stretch;
}

.phone-field {
  flex: 1;
}

.fetch-phone-btn {
  min-width: 118px;
  height: auto;
  flex-shrink: 0;
  color: #fff !important;
}

.field-hint,
.plate-inline-error {
  margin-top: 8px;
  font-size: 12px;
  line-height: 1.6;
}

.field-hint {
  color: var(--text-muted);
}

.plate-inline-error {
  color: #ee0a24;
}

.submit-wrap {
  margin-top: 24px;
}

.submit-btn {
  height: 50px;
  font-size: 16px;
  font-weight: 700;
  color: #fff !important;
  background: linear-gradient(135deg, #1cb76a 0%, #168d56 100%) !important;
  border: none !important;
  box-shadow: 0 14px 28px rgba(22, 141, 86, 0.22);
}

.custom-confirm-dialog {
  padding: 28px 20px 22px;
}

.confirm-icon {
  display: flex;
  justify-content: center;
  margin-bottom: 10px;
}

.confirm-title {
  text-align: center;
  color: var(--text-primary);
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 16px;
}

.confirm-details {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 22px;
}

.detail-item,
.detail-row {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 14px;
  border-radius: 16px;
  background: #f6f9ff;
  border: 1px solid #e1ebfb;
}

.detail-label,
.detail-row span {
  color: var(--text-secondary);
  font-size: 14px;
}

.detail-value,
.detail-row strong {
  color: var(--text-primary);
  font-size: 14px;
  font-weight: 700;
  text-align: right;
  word-break: break-all;
}

.detail-row--block {
  flex-direction: column;
}

.detail-image {
  width: 100%;
  border-radius: 16px;
  margin-top: 8px;
}

.confirm-actions,
.datetime-popup__actions {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.picker-popup,
.record-popup,
.datetime-popup {
  padding: 18px;
}

.datetime-popup {
  padding-bottom: calc(18px + env(safe-area-inset-bottom));
}

.datetime-popup__actions {
  margin-top: 14px;
}

.picker-popup__header,
.record-popup__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.picker-popup__header h3,
.record-popup__header h3 {
  margin: 0;
  color: var(--text-primary);
  font-size: 18px;
}

.picker-option,
.record-list__item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 14px 4px;
  border-bottom: 1px solid #eef3f9;
}

.record-list__title {
  color: var(--text-primary);
  font-size: 14px;
  font-weight: 700;
}

.record-list__desc {
  color: var(--text-secondary);
  font-size: 13px;
}

.back-btn {
  width: 42px;
  height: 42px;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.9);
  color: #1d2a39;
  font-size: 18px;
  box-shadow: 0 8px 20px rgba(25, 41, 72, 0.08);
}

.success-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 16px 20px 40px;
}

.success-icon {
  margin-bottom: 32px;
}

.success-title {
  color: #c9342f;
  font-size: 28px;
  line-height: 1.35;
  font-weight: 700;
  margin-bottom: 18px;
}

.success-meta {
  color: #c9342f;
  font-size: 20px;
  line-height: 1.6;
  font-weight: 700;
}

.success-links {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 36px 0 120px;
  color: #60779e;
  font-size: 16px;
}

.success-links button {
  border: none;
  background: transparent;
  color: #60779e;
  font-size: 16px;
  text-decoration: underline;
}

.success-action {
  width: min(520px, 100%);
  height: 54px;
  font-size: 18px;
  color: #fff !important;
  background: linear-gradient(135deg, #22b36b 0%, #168d56 100%) !important;
  border: none !important;
}

.form-block ::v-deep .__vcp-number-box ul {
  height: 60px;
  gap: 6px;
  justify-content: space-between;
  overflow: visible;
}

.form-block ::v-deep .__vcp-number-box ul > li {
  flex: 0 0 calc((100% - 42px) / 8);
  margin-right: 0 !important;
  font-size: 22px;
  line-height: 60px;
  border-radius: 10px;
  box-sizing: border-box;
  overflow: hidden;
  border: 1px solid #c7d3e6 !important;
  background: #fff;
}

.form-block ::v-deep .__vcp-number-box ul > li::after {
  display: none !important;
}

.form-block ::v-deep .__vcp-number-box ul > li.active {
  border-color: #1556c7 !important;
  box-shadow: 0 0 0 2px rgba(21, 86, 199, 0.12);
}

.form-block ::v-deep .__vcp-keyboard-panel {
  padding: 8px 8px calc(10px + env(safe-area-inset-bottom)) 8px;
  background: #dfe6f2;
  box-shadow: 0 -10px 30px rgba(15, 41, 88, 0.16);
}

.form-block ::v-deep .__vcp-keyboard-panel > ul {
  padding: 64px 0 0 0;
}

.form-block ::v-deep .__vcp-keyboard-panel > ul > li {
  height: 52px;
  margin: 0 0 8px 0;
  line-height: 52px;
  box-sizing: border-box;
}

.form-block ::v-deep .__vcp-keyboard-panel > ul > li > a {
  min-height: 52px;
  margin: 0 6px 0 0;
  font-size: 20px;
  line-height: 52px;
  border-radius: 12px;
}

@media (max-width: 767px) {
  .access-form-page,
  .success-view {
    padding: 14px;
  }

  .record-card,
  .form-card {
    border-radius: 20px;
  }

  .phone-row {
    align-items: flex-end;
    flex-wrap: wrap;
  }

  .fetch-phone-btn {
    min-width: 96px;
    min-height: 36px;
    padding: 0 12px;
    margin-left: auto;
    font-size: 13px;
  }

  .detail-item,
  .detail-row {
    flex-direction: column;
    gap: 6px;
  }

  .detail-value,
  .detail-row strong {
    text-align: left;
  }

  .success-title {
    font-size: 24px;
  }

  .success-meta {
    font-size: 18px;
  }

  .success-links {
    flex-wrap: wrap;
    justify-content: center;
    margin-bottom: 80px;
  }
}
</style>
